import React, { createContext, useState, useEffect, useContext } from "react";

const AccessibilityContext = createContext();

export const useAccessibility = () => useContext(AccessibilityContext);

export const AccessibilityProvider = ({ children }) => {
  // Load settings from localStorage or use defaults
  const [settings, setSettings] = useState(() => {
    const savedSettings = localStorage.getItem("accessibilitySettings");
    return savedSettings
      ? JSON.parse(savedSettings)
      : {
          fontSize: "normal", // normal, large, extra-large
          contrast: "normal", // normal, high-contrast, grayscale, inverted
          cursor: "normal", // normal, big, reading-guide
          animations: "on", // on, off
          links: "normal", // normal, highlight
        };
  });

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("accessibilitySettings", JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

  const applySettings = (currentSettings) => {
    const root = document.documentElement;
    const body = document.body;

    // Reset all classes first
    body.classList.remove(
      "acc-font-large",
      "acc-font-xl",
      "acc-contrast-high",
      "acc-contrast-gray",
      "acc-contrast-invert",
      "acc-cursor-big",
      "acc-cursor-guide",
      "acc-anim-off",
      "acc-links-highlight"
    );

    // Apply Font Size
    if (currentSettings.fontSize === "large") body.classList.add("acc-font-large");
    if (currentSettings.fontSize === "extra-large") body.classList.add("acc-font-xl");

    // Apply Contrast
    if (currentSettings.contrast === "high-contrast") body.classList.add("acc-contrast-high");
    if (currentSettings.contrast === "grayscale") body.classList.add("acc-contrast-gray");
    if (currentSettings.contrast === "inverted") body.classList.add("acc-contrast-invert");

    // Apply Cursor
    if (currentSettings.cursor === "big") body.classList.add("acc-cursor-big");
    if (currentSettings.cursor === "reading-guide") body.classList.add("acc-cursor-guide");

    // Apply Animations
    if (currentSettings.animations === "off") body.classList.add("acc-anim-off");

    // Apply Links
    if (currentSettings.links === "highlight") body.classList.add("acc-links-highlight");
  };

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const resetSettings = () => {
    setSettings({
      fontSize: "normal",
      contrast: "normal",
      cursor: "normal",
      animations: "on",
      links: "normal",
    });
  };

  return (
    <AccessibilityContext.Provider value={{ settings, updateSetting, resetSettings }}>
      {children}
    </AccessibilityContext.Provider>
  );
};
